import React, { useEffect } from "react";
import ReactTooltip from "react-tooltip";
import { twMerge } from "tailwind-merge";

import { useDropdownMenu } from "./context";

interface DropdownMenuItemProps {
  children: React.ReactNode;
  /** Callback when item is selected */
  onSelect?: (event: Event) => void;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether to close menu on select (default: true) */
  closeOnSelect?: boolean;
  /** Tooltip shown on hover when item is disabled */
  tooltip?: string;
}

/**
 * DropdownMenuItem - A selectable item within the dropdown
 */
export function DropdownMenuItem({
  children,
  onSelect,
  disabled = false,
  className = "",
  closeOnSelect = true,
  tooltip,
}: DropdownMenuItemProps) {
  const { close } = useDropdownMenu();

  // react-tooltip doesn't scan dynamically-mounted elements automatically.
  useEffect(() => {
    if (disabled && tooltip) ReactTooltip.rebuild();
  }, [disabled, tooltip]);

  const handleClick = (event: React.MouseEvent) => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    const nativeEvent = event.nativeEvent;
    onSelect?.(nativeEvent);

    if (closeOnSelect) {
      close();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const nativeEvent = new Event("select");
      onSelect?.(nativeEvent);

      if (closeOnSelect) {
        close();
      }
    }
  };

  return (
    <div
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={twMerge(
        "relative flex cursor-pointer select-none items-center gap-sm bg-primary px-md py-sm text-xs font-medium leading-lg text-primary outline-none transition-colors hover:bg-accent focus:bg-accent",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
    >
      {children}
      {/* Overlay captures hover for tooltip — pointer-events-auto overrides the
          inherited pointer-events-none from the parent, allowing react-tooltip
          to fire on the global-tooltip target. */}
      {disabled && tooltip && (
        <div
          className="absolute inset-0 pointer-events-auto"
          data-tip={tooltip}
          data-for="global-tooltip"
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </div>
  );
}
